import React from "react";

import Button from "components/Button";
import Modal from "components/Modal";

export interface OffPlatformEditChangeRow {
  label: string;
  from?: string;
  to?: string;
}

export interface OffPlatformEditRecomputedRow {
  label: string;
  value?: string;
}

export default function SelfFinanceOffPlatformEditConfirmationModal({
  isOpen,
  closeModal,
  onConfirm,
  onCloseModal,
  changes = [],
  recomputed = [],
  amountChanged = false,
  payoutDaysChanged = false
}: Readonly<{
  isOpen: boolean;
  closeModal: () => void;
  onConfirm?: () => void;
  onCloseModal?: () => void;
  changes?: OffPlatformEditChangeRow[];
  recomputed?: OffPlatformEditRecomputedRow[];
  amountChanged?: boolean;
  payoutDaysChanged?: boolean;
}>) {
  const handleCancel = () => {
    onCloseModal?.();
    closeModal();
  };

  const handleConfirm = () => {
    onConfirm?.();
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleCancel}
      size="sm"
      wrapperClass="!my-auto desktop:!min-h-fit px-4 desktop:px-6"
      dialogClass="!bg-white dark:!bg-card !border !border-gray-200 dark:!border-card !p-0 !max-h-[calc(100vh-2rem)] !overflow-y-auto rounded-2xl shadow-lg w-full"
      closeClass="!hidden">
      <div className="border-b border-gray-200 dark:border-white/10 px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-white/70">
          Self Finance (Off-Platform)
        </p>
        <h2 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white desktop:text-xl">
          Confirm funding request changes
        </h2>
      </div>

      <div className="px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-sm leading-relaxed text-gray-600 dark:text-white/75">
          Saving these changes re-runs the rate engine and updates the related carrier, withdrawal,
          and debtor invoice obligations and their line items.
        </p>

        {changes.length > 0 && (
          <div className="mt-4 rounded-xl bg-gray-100 dark:bg-black/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-white/70">
              Your changes
            </p>
            <div className="mt-2 space-y-1.5">
              {changes.map((change) => (
                <div
                  key={change.label}
                  className="flex items-center justify-between gap-3 text-sm text-gray-900 dark:text-white">
                  <span className="text-gray-600 dark:text-white/75">{change.label}</span>
                  <span className="font-medium">
                    {change.from} {"→"} {change.to}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {recomputed.length > 0 && (
          <div className="mt-3 rounded-xl bg-gray-100 dark:bg-black/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-white/70">
              Recalculated amounts
            </p>
            <div className="mt-2 space-y-1.5">
              {recomputed.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-3 text-sm text-gray-900 dark:text-white">
                  <span className="text-gray-600 dark:text-white/75">{row.label}</span>
                  <span className="font-medium">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {amountChanged && (
          <div className="mt-4 rounded-lg border border-warning bg-warning/20 px-3 py-2.5 text-xs leading-relaxed text-warning">
            The shipper invoice will be regenerated. You&apos;ll be prompted to resend it after
            saving.
          </div>
        )}

        {payoutDaysChanged && (
          <div className="mt-4 rounded-lg border border-warning bg-warning/20 px-3 py-2.5 text-xs leading-relaxed text-warning">
            The payout clock resets to now (the time of this adjustment), so the carrier&apos;s
            payout may exceed 30 days.
          </div>
        )}
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-gray-200 dark:border-white/10 px-4 py-4 desktop:flex-row desktop:justify-end desktop:gap-3 desktop:px-6">
        <Button
          type="button"
          rounded
          variant="outline"
          onClick={handleCancel}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[7rem] border-gray-300 dark:border-white/20 bg-transparent text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5">
          Cancel
        </Button>
        <Button
          type="button"
          rounded
          onClick={handleConfirm}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[10rem] !bg-primary dark:!bg-white !text-white dark:!text-card hover:!bg-primary/90 dark:hover:!bg-white/90">
          Confirm changes
        </Button>
      </div>
    </Modal>
  );
}
